import { useState } from 'react';
import { flushSync } from 'react-dom';
import {
  FormControl,
  FormLabel,
  Select,
  Button,
  Flex,
  Box,
  VStack,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';

import getDateFromString from 'helpers/getDateFromString';
import getDateString from 'helpers/getDateString';
import SectionCard from '../../components/SectionCard';
import ValidatedDateField from '../../components/ValidatedDateField';
import ItvReviewModal from '../../components/ItvReviewModal';
import {
  apiFetchPropertyEzitv,
  apiFetchItvPrefill,
  type Location,
  type ItvData,
} from 'api';
import { useAnswerCodes } from '../../hooks';
import { filterPrefillByConditionalRules } from '../../utils/itvPrefill';

interface EvaluationToolsCardProps {
  location: Location;
  locationIndex: number;
  quoteId: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  onSave?: () => Promise<void>;
  isUpdating?: boolean;
}

const EVALUATION_TOOL_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'biild_itv', label: 'BIILD ITV' },
  { value: 'iclarify', label: 'iClarify' },
];

const EvaluationToolsCard = ({
  location,
  locationIndex,
  quoteId,
  onUpdate,
  onSave,
  isUpdating,
}: EvaluationToolsCardProps) => {
  const toast = useToast();
  const { getHabRule } = useAnswerCodes();
  const [isFetchingEzitv, setIsFetchingEzitv] = useState(false);
  const [pendingItvData, setPendingItvData] = useState<ItvData | null>(null);
  const [originalFeatures, setOriginalFeatures] = useState<Record<
    string,
    string | number | null
  > | null>(null);
  const [pendingAddress, setPendingAddress] = useState<{
    street_address: string;
    city: string;
    postal_code: string;
    province: string;
  } | null>(null);
  const {
    isOpen: isItvModalOpen,
    onOpen: openItvModal,
    onClose: closeItvModal,
  } = useDisclosure();

  const handleDateChange = (date: Date | null) => {
    onUpdate(
      locationIndex,
      'evaluation_date',
      date ? getDateString(date) : null,
    );
  };

  const handleToolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate(locationIndex, 'home_evaluation_tool_used', e.target.value);
  };

  const handleFetchEzitv = async () => {
    const address = {
      street_address: location.address?.trim() || '',
      city: location.city?.trim() || '',
      postal_code: location.postal_code?.trim() || '',
      province: location.province?.trim() || '',
    };

    const missing = Object.entries(address)
      .filter(([, value]) => !value)
      .map(([key]) => key.replace('_', ' '));

    if (missing.length > 0) {
      toast({
        title: 'Missing address details',
        description: `Please complete the location address (${missing.join(', ')}) before running iClarify.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (pendingItvData && originalFeatures) {
      setPendingAddress(address);
      openItvModal();
      return;
    }

    setIsFetchingEzitv(true);
    try {
      const response = await apiFetchPropertyEzitv(quoteId, address);

      if (!response.found || !response.itv_data) {
        toast({
          title: 'No evaluation data found',
          description:
            response.error || 'iClarify returned no data for this address.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      const existingFeatures = location.itv_data?.ConstructionFeatures;
      const mergedItvData = existingFeatures
        ? {
            ...response.itv_data,
            ConstructionFeatures: {
              ...response.itv_data.ConstructionFeatures,
              ...existingFeatures,
            },
          }
        : response.itv_data;

      setPendingItvData(mergedItvData);
      setOriginalFeatures(response.original_features || null);
      setPendingAddress(address);
      openItvModal();
    } catch (error) {
      const serverError = (error as { error?: string })?.error;
      const message =
        serverError &&
        serverError !== 'Unhandled error' &&
        serverError !== 'No response'
          ? serverError
          : 'Failed to fetch iClarify evaluation. Please try again.';
      toast({
        title: 'iClarify lookup failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsFetchingEzitv(false);
    }
  };

  const handleItvApply = async (finalItvData: ItvData) => {
    onUpdate(locationIndex, 'itv_data', finalItvData);
    onUpdate(locationIndex, 'evaluation_date', getDateString(new Date()));
    onUpdate(locationIndex, 'home_evaluation_tool_used', 'iclarify');

    const replacementCost = finalItvData.Valuation?.ReplacementCost;
    if (replacementCost != null) {
      const roundedCost = Math.round(replacementCost);
      onUpdate(locationIndex, 'building_value', roundedCost);
      onUpdate(locationIndex, 'building_coverage', roundedCost);
    }

    let dwellingFieldsCount = 0;
    try {
      const prefillResponse = await apiFetchItvPrefill(quoteId, finalItvData);
      const applicable = filterPrefillByConditionalRules(
        { ...location, itv_data: finalItvData },
        prefillResponse.prefill || {},
        getHabRule,
      );

      const COVERAGE_FIELDS = [
        'swimming_pool_enabled',
        'swimming_pool_type',
        'swimming_pool_material',
        'solar_panel_enabled',
      ];

      const coverageUpdates: Record<string, unknown> = {};
      const locationUpdates: Record<string, unknown> = {};

      Object.entries(applicable).forEach(([field, value]) => {
        if (COVERAGE_FIELDS.includes(field)) {
          coverageUpdates[field] = value;
        } else {
          locationUpdates[field] = value;
        }
      });

      flushSync(() => {
        Object.entries(locationUpdates).forEach(([field, value]) => {
          onUpdate(locationIndex, field as keyof Location, value);
        });

        if (Object.keys(coverageUpdates).length > 0) {
          const mergedCoverages = {
            ...location.coverages,
            ...coverageUpdates,
          };
          onUpdate(locationIndex, 'coverages', mergedCoverages);
        }

        if (prefillResponse.locked_fields?.length) {
          onUpdate(
            locationIndex,
            'itv_locked_fields',
            prefillResponse.locked_fields,
          );
        }
      });
      dwellingFieldsCount = Object.keys(applicable).length;
    } catch {
      // Prefill failed but ITV data was saved
    }

    const description =
      dwellingFieldsCount > 0
        ? `Filled ${dwellingFieldsCount} dwelling fields from evaluation`
        : 'Home evaluation data applied';

    closeItvModal();
    setPendingItvData(null);
    setPendingAddress(null);

    if (onSave) {
      await onSave();
    }

    toast({
      title: 'iClarify evaluation applied',
      description,
      status: 'success',
      duration: 4000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleModalClose = () => {
    closeItvModal();
  };

  return (
    <SectionCard title="Evaluation Tools">
      <Flex
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        align={{ base: 'stretch', md: 'flex-end' }}
      >
        <Flex flex={1} gap={4} direction={{ base: 'column', sm: 'row' }}>
          <Box flex={1}>
            <ValidatedDateField
              itemPath="location"
              fieldPath="evaluation_date"
              label="Last Home Evaluation"
              date={getDateFromString(location.evaluation_date || '')}
              onChange={handleDateChange}
              entity={location as unknown as Record<string, unknown>}
              isDisabled={isUpdating || isFetchingEzitv}
            />
          </Box>

          <Box flex={1}>
            <FormControl>
              <FormLabel fontSize="sm" whiteSpace="nowrap">
                Home Evaluation Tool Used
              </FormLabel>
              <Select
                variant="main"
                size="sm"
                value={location.home_evaluation_tool_used || ''}
                onChange={handleToolChange}
                isDisabled={isUpdating || isFetchingEzitv}
              >
                {EVALUATION_TOOL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Flex>

        <VStack
          spacing={2}
          align="stretch"
          minW={{ base: 'auto', md: '120px' }}
        >
          <Button variant="outline" size="sm" isDisabled>
            BIILD ITV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleFetchEzitv}
            isLoading={isFetchingEzitv}
            loadingText="Fetching..."
            isDisabled={isUpdating}
          >
            iClarify
          </Button>
        </VStack>
      </Flex>

      {pendingItvData && pendingAddress && (
        <ItvReviewModal
          isOpen={isItvModalOpen}
          onClose={handleModalClose}
          itvData={pendingItvData}
          address={pendingAddress}
          quoteId={quoteId}
          onApply={handleItvApply}
          originalFeatures={originalFeatures ?? undefined}
        />
      )}
    </SectionCard>
  );
};

export default EvaluationToolsCard;
